import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceToolsSection = () => {
  const [selectedTool, setSelectedTool] = useState('checklist');

  const tools = [
    { id: 'checklist', label: 'Transfer Checklist', icon: 'CheckSquare' },
    { id: 'calculator', label: 'Fee Calculator', icon: 'Calculator' },
    { id: 'timeline', label: 'Process Timeline', icon: 'Clock' },
    { id: 'documents', label: 'Document Templates', icon: 'FileText' }
  ];

  const checklistItems = [
    {
      category: "Pre-Transfer Requirements",
      items: [
        { text: "Player contract review and termination clauses", checked: false },
        { text: "Current club release confirmation", checked: false },
        { text: "Medical examination scheduling", checked: false },
        { text: "Work permit eligibility assessment", checked: false }
      ]
    },
    {
      category: "Documentation Preparation",
      items: [
        { text: "International Transfer Certificate request", checked: false },
        { text: "Player passport and visa documents", checked: false },
        { text: "Previous contract copies", checked: false },
        { text: "Training compensation documentation", checked: false }
      ]
    },
    {
      category: "FIFA TMS Registration",
      items: [
        { text: "TMS account verification", checked: false },
        { text: "Transfer agreement upload", checked: false },
        { text: "All parties digital signatures", checked: false },
        { text: "Solidarity payment calculations", checked: false }
      ]
    },
    {
      category: "Post-Transfer Compliance",
      items: [
        { text: "New association registration", checked: false },
        { text: "Work permit application submission", checked: false },
        { text: "Insurance coverage activation", checked: false },
        { text: "Financial fair play reporting", checked: false }
      ]
    }
  ];

  const documentTemplates = [
    {
      name: "Transfer Agreement Template",
      description: "Standard FIFA-compliant transfer agreement with all required clauses",
      icon: "FileSignature",
      format: "DOCX"
    },
    {
      name: "ITC Request Form",
      description: "International Transfer Certificate request template for FIFA TMS",
      icon: "FileCheck",
      format: "PDF"
    },
    {
      name: "Player Consent Form",
      description: "GDPR-compliant player data processing consent documentation",
      icon: "UserCheck",
      format: "PDF"
    },
    {
      name: "Medical Report Template",
      description: "Standardized medical examination report format",
      icon: "Stethoscope",
      format: "DOCX"
    },
    {
      name: "Work Permit Application",
      description: "UK GBE application template with points calculation guide",
      icon: "Briefcase",
      format: "PDF"
    },
    {
      name: "Solidarity Payment Calculator",
      description: "Excel template for calculating solidarity mechanism payments",
      icon: "Calculator",
      format: "XLSX"
    }
  ];

  const timelineSteps = [
    { phase: "Initial Contact", duration: "Week 1-2", tasks: ["Player identification", "Initial club approach", "Interest confirmation"] },
    { phase: "Negotiation", duration: "Week 3-4", tasks: ["Terms discussion", "Fee negotiation", "Contract drafting"] },
    { phase: "Documentation", duration: "Week 5-6", tasks: ["Medical examination", "Document preparation", "TMS registration"] },
    { phase: "Approval", duration: "Week 7-8", tasks: ["FIFA approval", "Work permit processing", "Association registration"] },
    { phase: "Completion", duration: "Week 9-10", tasks: ["Contract signing", "Payment processing", "Player integration"] }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-muted">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4 md:mb-6">
            <Icon name="Wrench" size={20} color="var(--color-primary)" />
            <span className="text-sm md:text-base font-medium text-primary">Interactive Tools</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 md:mb-6 font-headline">
            Compliance Tools & Resources
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Access our comprehensive suite of compliance tools, checklists, and templates to streamline your transfer processes.
          </p>
        </div>

        <div className="mb-8 md:mb-10">
          <div className="flex flex-wrap gap-3 md:gap-4 justify-center">
            {tools?.map((tool) => (
              <button
                key={tool?.id}
                onClick={() => setSelectedTool(tool?.id)}
                className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  selectedTool === tool?.id
                    ? 'bg-primary text-primary-foreground shadow-lg scale-105'
                    : 'bg-card text-foreground border border-border hover:border-primary hover:bg-background'
                }`}
              >
                <Icon name={tool?.icon} size={20} />
                <span className="text-sm md:text-base">{tool?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {selectedTool === 'checklist' && (
          <div className="bg-card rounded-xl border border-border p-6 md:p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="CheckSquare" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground font-headline">
                  International Transfer Checklist
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Complete guide for FIFA-compliant transfer processes
                </p>
              </div>
            </div>

            <div className="space-y-6 md:space-y-8">
              {checklistItems?.map((section, sectionIndex) => (
                <div key={sectionIndex}>
                  <h4 className="text-lg md:text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <span className="text-sm font-bold text-primary">{sectionIndex + 1}</span>
                    </div>
                    {section?.category}
                  </h4>
                  <div className="space-y-3 pl-0 md:pl-10">
                    {section?.items?.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-start gap-3 p-3 md:p-4 bg-muted rounded-lg hover:bg-background transition-colors duration-300">
                        <div className="w-5 h-5 border-2 border-primary rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                          {item?.checked && <Icon name="Check" size={14} color="var(--color-primary)" />}
                        </div>
                        <span className="text-sm md:text-base text-foreground">{item?.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-border">
              <Button variant="default" iconName="Download" iconPosition="left" className="w-full md:w-auto">
                Download Complete Checklist
              </Button>
            </div>
          </div>
        )}

        {selectedTool === 'documents' && (
          <div className="bg-card rounded-xl border border-border p-6 md:p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                <Icon name="FileText" size={24} color="var(--color-secondary)" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground font-headline">
                  Document Templates Library
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Ready-to-use templates for all transfer documentation needs
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {documentTemplates?.map((template, index) => (
                <div
                  key={index}
                  className="p-4 md:p-6 bg-muted rounded-lg border border-border hover:border-primary hover:shadow-md transition-all duration-300 group"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-card rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <Icon name={template?.icon} size={24} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base md:text-lg font-bold text-foreground mb-1">
                        {template?.name}
                      </h4>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {template?.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-primary px-3 py-1 bg-primary/10 rounded-full">
                      {template?.format}
                    </span>
                    <Button variant="ghost" size="sm" iconName="Download" iconPosition="left">
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTool === 'timeline' && (
          <div className="bg-card rounded-xl border border-border p-6 md:p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="Clock" size={24} color="var(--color-accent)" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground font-headline">
                  Transfer Process Timeline
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Typical timeline for international transfer completion
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-8 top-0 bottom-0 w-0.5 bg-border"></div>
              
              <div className="space-y-8 md:space-y-10">
                {timelineSteps?.map((step, index) => (
                  <div key={index} className="relative pl-12 md:pl-20">
                    <div className="absolute left-0 md:left-4 w-8 h-8 md:w-12 md:h-12 bg-primary rounded-full flex items-center justify-center border-4 border-card">
                      <span className="text-xs md:text-sm font-bold text-primary-foreground">{index + 1}</span>
                    </div>
                    
                    <div className="bg-muted rounded-lg p-4 md:p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
                        <h4 className="text-lg md:text-xl font-bold text-foreground">
                          {step?.phase}
                        </h4>
                        <span className="text-sm font-medium text-primary px-3 py-1 bg-primary/10 rounded-full w-fit">
                          {step?.duration}
                        </span>
                      </div>
                      <ul className="space-y-2">
                        {step?.tasks?.map((task, taskIndex) => (
                          <li key={taskIndex} className="flex items-center gap-2 text-sm md:text-base text-muted-foreground">
                            <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                            {task}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 p-4 md:p-6 bg-warning/10 rounded-lg border border-warning/20">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={24} color="var(--color-warning)" className="flex-shrink-0" />
                <div>
                  <h4 className="text-base md:text-lg font-bold text-foreground mb-2">
                    Timeline Variations
                  </h4>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Actual timelines may vary based on transfer complexity, work permit requirements, and registration window constraints. Youth transfers and cross-continental moves typically require additional time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedTool === 'calculator' && (
          <div className="bg-card rounded-xl border border-border p-6 md:p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="Calculator" size={24} color="var(--color-success)" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground font-headline">
                  Transfer Fee Calculator
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Estimate total transfer costs including all FIFA-mandated payments
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Transfer Fee (EUR)
                  </label>
                  <input
                    type="number"
                    placeholder="Enter transfer fee"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Player Age
                  </label>
                  <input
                    type="number"
                    placeholder="Enter player age"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Training Years
                  </label>
                  <input
                    type="number"
                    placeholder="Years at training clubs"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <Button variant="default" fullWidth iconName="Calculator" iconPosition="left">
                  Calculate Total Cost
                </Button>
              </div>

              <div className="space-y-4">
                <div className="p-4 md:p-6 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Base Transfer Fee</span>
                    <span className="text-lg font-bold text-foreground">€0.00</span>
                  </div>
                </div>

                <div className="p-4 md:p-6 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Solidarity Contribution (5%)</span>
                    <span className="text-lg font-bold text-foreground">€0.00</span>
                  </div>
                </div>

                <div className="p-4 md:p-6 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Training Compensation</span>
                    <span className="text-lg font-bold text-foreground">€0.00</span>
                  </div>
                </div>

                <div className="p-4 md:p-6 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Agent Commission (7%)</span>
                    <span className="text-lg font-bold text-foreground">€0.00</span>
                  </div>
                </div>

                <div className="p-4 md:p-6 bg-primary/10 rounded-lg border-2 border-primary">
                  <div className="flex items-center justify-between">
                    <span className="text-base font-medium text-foreground">Total Estimated Cost</span>
                    <span className="text-2xl font-bold text-primary">€0.00</span>
                  </div>
                </div>

                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    * Estimates based on FIFA regulations. Actual costs may vary based on specific circumstances and negotiations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ComplianceToolsSection;